import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UserCheck, UserX } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguagePath } from "@/utils/language";

interface StudentDetailsProps {
  student: {
    id: string;
    first_name: string;
    last_name: string;
    email?: string;
    phone?: string;
    birth_date?: string;
    goals?: string;
    notes?: string;
    status?: string;
  };
  isOpen: boolean;
  onClose: () => void;
}

export const StudentDetails = ({
  student,
  isOpen,
  onClose,
}: StudentDetailsProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslations("students");
  const { getPath } = useLanguagePath();

  const toggleStatus = async () => {
    try {
      console.log("Toggling student status:", student.id);
      const newStatus = student.status === "active" ? "inactive" : "active";

      const { error } = await supabase
        .from("students")
        .update({ status: newStatus })
        .eq("id", student.id);

      if (error) throw error;

      toast.success(
        newStatus === "active" ? t("status.activated") : t("status.deactivated")
      );
      queryClient.invalidateQueries({ queryKey: ["students"] });
      onClose();
    } catch (error: any) {
      console.error("Error toggling student status:", error);
      toast.error(error.message || t("status.error"));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {student.first_name} {student.last_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {student.email && (
            <div>
              <h4 className="font-medium">Email</h4>
              <p className="text-sm text-muted-foreground">{student.email}</p>
            </div>
          )}
          {student.phone && (
            <div>
              <h4 className="font-medium">Telefone</h4>
              <p className="text-sm text-muted-foreground">{student.phone}</p>
            </div>
          )}
          {student.birth_date && (
            <div>
              <h4 className="font-medium">Data de Nascimento</h4>
              <p className="text-sm text-muted-foreground">
                {new Date(student.birth_date).toLocaleDateString("pt-PT")}
              </p>
            </div>
          )}
          {student.goals && (
            <div>
              <h4 className="font-medium">Objetivos</h4>
              <p className="text-sm text-muted-foreground">{student.goals}</p>
            </div>
          )}
          {student.notes && (
            <div>
              <h4 className="font-medium">Notas</h4>
              <p className="text-sm text-muted-foreground">{student.notes}</p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(getPath(`/dashboard/students/edit/${student.id}`))}
            >
              {t("actions.edit")}
            </Button>
            <Button
              variant={student.status === "active" ? "destructive" : "default"}
              onClick={toggleStatus}
            >
              {student.status === "active" ? (
                <>
                  <UserX className="mr-2 h-4 w-4" />
                  Desativar Aluno
                </>
              ) : (
                <>
                  <UserCheck className="mr-2 h-4 w-4" />
                  Ativar Aluno
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};